package com.anjuros.website.templates.template9.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.px

/**
 * Small decorative SVG pieces used across template 9 (hearts, stars, squiggles, waves).
 * Colors are passed as hex strings so they can follow the placeholder theme (getColorHex()).
 */

@Composable
fun Template9HeartIcon(
    modifier: Modifier = Modifier,
    size: Int = 16,
    color: String,
    filled: Boolean = false
) {
    Svg(
        attrs = modifier
            .size(size.px)
            .toAttrs {
                attr("viewBox", "0 0 24 24")
                attr("fill", if (filled) color else "none")
                attr("stroke", color)
                attr("stroke-width", "2.5")
                attr("stroke-linecap", "round")
                attr("stroke-linejoin", "round")
                attr("aria-hidden", "true")
            }
    ) {
        Path(attrs = {
            attr(
                "d",
                "M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
            )
        })
    }
}

@Composable
fun Template9StarIcon(
    modifier: Modifier = Modifier,
    size: Int = 18,
    color: String
) {
    Svg(
        attrs = modifier
            .size(size.px)
            .toAttrs {
                attr("viewBox", "0 0 24 24")
                attr("fill", color)
                attr("stroke", color)
                attr("stroke-width", "1")
                attr("stroke-linejoin", "round")
                attr("aria-hidden", "true")
            }
    ) {
        Path(attrs = {
            attr(
                "d",
                "M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"
            )
        })
    }
}

/** Hand-drawn underline shown under section titles. */
@Composable
fun Template9Squiggle(
    modifier: Modifier = Modifier,
    width: Int = 90,
    color: String
) {
    Svg(
        attrs = modifier
            .size(width.px, (width / 8).px)
            .toAttrs {
                attr("viewBox", "0 0 120 15")
                attr("fill", "none")
                attr("stroke", color)
                attr("stroke-width", "4")
                attr("stroke-linecap", "round")
                attr("aria-hidden", "true")
            }
    ) {
        Path(attrs = { attr("d", "M3 10 C 30 3, 62 14, 117 5") })
    }
}

/** Three little "spark" strokes – used next to the hero title and in corners. */
@Composable
fun Template9Sparks(
    modifier: Modifier = Modifier,
    size: Int = 40,
    color: String
) {
    Svg(
        attrs = modifier
            .size(size.px)
            .toAttrs {
                attr("viewBox", "0 0 40 40")
                attr("fill", "none")
                attr("stroke", color)
                attr("stroke-width", "3")
                attr("stroke-linecap", "round")
                attr("aria-hidden", "true")
            }
    ) {
        Path(attrs = { attr("d", "M6 24 L14 27") })
        Path(attrs = { attr("d", "M10 10 L17 17") })
        Path(attrs = { attr("d", "M24 4 L25 13") })
    }
}

/** Dashed curvy trail – decoration in the hero. */
@Composable
fun Template9Trail(
    modifier: Modifier = Modifier,
    color: String
) {
    Svg(
        attrs = modifier
            .size(90.px, 220.px)
            .toAttrs {
                attr("viewBox", "0 0 90 220")
                attr("fill", "none")
                attr("stroke", color)
                attr("stroke-width", "2.5")
                attr("stroke-linecap", "round")
                attr("stroke-dasharray", "6 8")
                attr("aria-hidden", "true")
            }
    ) {
        Path(attrs = { attr("d", "M70 4 C 10 30, 80 70, 30 100 S 10 170, 60 180 S 40 215, 4 216") })
    }
}

/**
 * Full width wave used to give sections a soft organic edge.
 * [top] = true draws a wave that sits ABOVE a coloured block (filled at the bottom).
 */
@Composable
fun Template9Wave(
    modifier: Modifier = Modifier,
    color: String,
    top: Boolean = true
) {
    Svg(
        attrs = modifier
            .fillMaxWidth()
            .display(DisplayStyle.Block)
            .toAttrs {
                attr("viewBox", "0 0 1440 80")
                attr("preserveAspectRatio", "none")
                attr("aria-hidden", "true")
            }
    ) {
        Path(attrs = {
            attr(
                "d",
                if (top)
                    "M0,46 C240,86 480,6 720,36 C960,66 1200,14 1440,40 L1440,81 L0,81 Z"
                else
                    "M0,-1 L1440,-1 L1440,34 C1200,74 960,8 720,40 C480,72 240,4 0,36 Z"
            )
            attr("fill", color)
        })
    }
}
